import { type MouseEvent, useCallback } from "react";

import type { ScopedThreadRef } from "@afar-research/contracts";

import { stackedThreadToast, toastManager } from "../components/ui/toast";
import {
  parseLinkPullRequestInput,
  projectPullRequestRepository,
} from "../components/pullRequest/linkPullRequestTarget.logic";
import { useOpenLink } from "../browser/useOpenLink";
import { useRightPanelStore } from "../rightPanelStore";
import { useProjects } from "../state/entities";

/** Following T3Code: a modifier-click leaves the link alone and opens it on the host. */
export function shouldOpenPullRequestExternally(
  event: Pick<MouseEvent<HTMLElement>, "metaKey" | "ctrlKey">,
): boolean {
  return event.metaKey || event.ctrlKey;
}

/**
 * Returns a click handler for a pull request link.
 *
 * Given a thread, and a pull request one of that thread's Environment's Projects can read, the
 * link opens beside the conversation: a reader following a link is reading the thread, and
 * should still be reading it afterwards. Anything else - another Environment's repository, a
 * host nothing here is checked out from, a modifier-click - stays the ordinary link it looks
 * like and opens in the system browser.
 *
 * Stops event propagation/default so activating the link does not also trigger an enclosing row
 * or trigger, and surfaces a toast when the local API is unavailable or the open fails.
 */
export function useOpenPrLink(defaultThreadRef?: ScopedThreadRef) {
  const projects = useProjects();
  const openLink = useOpenLink(defaultThreadRef);
  return useCallback(
    (event: MouseEvent<HTMLElement>, prUrl: string, targetThreadRef?: ScopedThreadRef) => {
      // The thread may be the caller's own or the row's, so it is taken per call where a list
      // shares one handler across many threads.
      const threadRef = targetThreadRef ?? defaultThreadRef;
      if (threadRef !== undefined && !shouldOpenPullRequestExternally(event)) {
        const parsed = parseLinkPullRequestInput(prUrl, null);
        // The Project is resolved here rather than on the panel: a lookalike hostname matches
        // none and stays a link, and a panel beside a thread can only read on that thread's own
        // Environment however promising another one's checkout looks.
        // The repository's own checkout is preferred, since it is the one whose credentials
        // certainly reach the pull request; any other checkout on the same host can still lend
        // its own, which is what makes a repository nobody cloned readable at all.
        const onHost =
          parsed.kind !== "target"
            ? []
            : projects.filter((candidate) => {
                if (candidate.environmentId !== threadRef.environmentId) return false;
                const repository = projectPullRequestRepository(candidate.repositoryIdentity);
                return repository?.host === parsed.target.host;
              });
        const project =
          parsed.kind !== "target"
            ? undefined
            : (onHost.find(
                (candidate) =>
                  projectPullRequestRepository(candidate.repositoryIdentity)?.repository ===
                  parsed.target.repository,
              ) ?? onHost[0]);
        if (parsed.kind === "target" && project !== undefined) {
          event.preventDefault();
          event.stopPropagation();
          useRightPanelStore.getState().openPullRequest(threadRef, {
            projectId: project.id,
            host: parsed.target.host,
            repository: parsed.target.repository,
            number: parsed.target.number,
            url: parsed.target.url,
          });
          return;
        }
      }
      event.preventDefault();
      event.stopPropagation();

      // No Project to show it in, so it is an ordinary link and follows the
      // "Open links in" setting; the modifier still forces the system browser.
      void openLink(prUrl, { event, ...(threadRef === undefined ? {} : { threadRef }) }).catch(
        (error: unknown) => {
          console.error(error);
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: "Unable to open pull request link",
              description: error instanceof Error ? error.message : "An error occurred.",
            }),
          );
        },
      );
    },
    [defaultThreadRef, openLink, projects],
  );
}
